import { AlertTriangle, Info, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { FramingAvailability } from "../framingInspector";
import { framingEvidenceState } from "./framingEvidenceStates";

/**
 * Typed partial/error evidence state (CROP-WB-012): what is known, why, and
 * what the operator can do next. Never a generic spinner or failure.
 */
export function FramingEvidenceState({
  availability,
  rawCode,
  onRetry,
  compact,
}: {
  availability: FramingAvailability;
  rawCode?: string | null;
  onRetry?: (() => void) | null;
  compact?: boolean;
}) {
  const state = framingEvidenceState(availability);
  const Icon =
    state.tone === "destructive"
      ? ShieldAlert
      : state.tone === "warning"
        ? AlertTriangle
        : Info;
  return (
    <div
      role="status"
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed p-4 text-center",
        compact ? "text-xs" : "text-sm",
        state.tone === "destructive" && "border-destructive/60",
        state.tone === "warning" && "border-amber-400/60",
      )}
    >
      <Icon
        className={cn(
          "size-5",
          state.tone === "destructive"
            ? "text-destructive"
            : state.tone === "warning"
              ? "text-amber-500"
              : "text-muted-foreground",
        )}
      />
      <span className="font-medium">{state.title}</span>
      <span className="max-w-72 text-muted-foreground">
        {state.explanation}
      </span>
      {rawCode && availability === "unknown" ? (
        <code className="text-[10px] text-muted-foreground">{rawCode}</code>
      ) : null}
      {(state.action === "retry" || state.action === "refresh_once") &&
      onRetry ? (
        <Button size="sm" variant="outline" onClick={onRetry}>
          {state.action === "refresh_once" ? "Refresh media" : "Retry"}
        </Button>
      ) : null}
    </div>
  );
}
